import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFiguTour } from "@/components/figutour/FiguTourProvider";
import { useTypewriter } from "@/components/figutour/useTypewriter";
import { useI18n } from "@/lib/I18nContext";

const BUBBLE_WIDTH = 320;
const BUBBLE_MARGIN_TO_FAB = 12;

/**
 * FiguTour v2 bubble (Phase 16 rewrite).
 *
 * Anchored to the Robo-Ball FAB via rAF-tracked getBoundingClientRect().
 * All button handlers pull LATEST callbacks from context each render —
 * no stale closures.
 *
 * Controls:
 *   • Back  — visible on steps 2+, hidden on step 1
 *   • Next  — visible on steps 1-4
 *   • Finish — replaces Next on step 5, calls endTour({completed:true})
 *
 * Buttons are NEVER disabled by the typewriter — clicking Next always
 * advances immediately.
 */
export default function FiguTourBubble() {
  const tour = useFiguTour();
  const { t } = useI18n();
  const {
    currentStep,
    stepIndex,
    totalSteps,
    nextStep,
    prevStep,
    endTour,
  } = tour;

  const [anchor, setAnchor] = useState(null);

  // Phase 19b — pull bubble copy from i18n via `bubbleKey`. Backward
  // compat: if a legacy step still ships `bubble`, use it verbatim.
  const bubbleText = currentStep
    ? currentStep.bubbleKey
      ? t(currentStep.bubbleKey)
      : currentStep.bubble || ""
    : "";

  const { display, isComplete } = useTypewriter(bubbleText, {
    charsPerSecond: 33,
  });

  // Debug — surface handler identity changes for traceability.
  const handlerLogRef = useRef({ nextStep: null, prevStep: null });
  useEffect(() => {
    handlerLogRef.current = { nextStep, prevStep };
    // eslint-disable-next-line no-console
    console.debug("[figutour] bubble handlers refreshed", {
      step: currentStep?.id,
    });
  }, [nextStep, prevStep, currentStep]);

  useEffect(() => {
    if (!currentStep) return undefined;
    let raf = null;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const fab = document.querySelector('[data-testid="roboball-fab"]');
      if (fab) {
        const r = fab.getBoundingClientRect();
        setAnchor({
          left: r.left + r.width / 2,
          top: r.top,
          bottom: r.bottom,
          width: r.width,
        });
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [currentStep]);

  if (!currentStep) return null;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  // Phase 19c/d — Mobile override for Step 3 (radar). At <768 px the
  // Radar tab is a tiny bottom-nav icon; the default "above FAB" bubble
  // placement collides with the bottom-nav strip on iPhone SE (375×667).
  // Force the bubble to the TOP of the viewport with `data-place="mobile-top"`.
  const isMobile = vw < 768;
  const mobileTopOverride =
    isMobile && currentStep.id === "radar";

  let bubbleLeft = 16;
  let bubbleTop = vh - 240;
  let placeBelow = false;
  if (mobileTopOverride) {
    bubbleLeft = 16;
    bubbleTop = 24; // just below status bar / notch safe area
  } else if (anchor) {
    bubbleLeft = anchor.left - BUBBLE_WIDTH / 2;
    bubbleLeft = Math.max(16, Math.min(vw - BUBBLE_WIDTH - 16, bubbleLeft));
    const estimatedBubbleHeight = 200;
    if (anchor.top - BUBBLE_MARGIN_TO_FAB - estimatedBubbleHeight >= 16) {
      bubbleTop = anchor.top - BUBBLE_MARGIN_TO_FAB - estimatedBubbleHeight;
    } else {
      bubbleTop = anchor.bottom + BUBBLE_MARGIN_TO_FAB;
      placeBelow = true;
    }
  }

  const isFirst = stepIndex === 0;
  const isLast = stepIndex >= totalSteps - 1;

  // Handlers close over the LATEST context values (destructured this render)
  // — no stale-closure risk.
  const onNextClick = () => {
    if (isLast) {
      endTour({ completed: true });
    } else {
      nextStep();
    }
  };
  const onBackClick = () => prevStep();
  const onSkipClick = () => endTour({ completed: false });

  const node = (
    <div
      className="figutour-bubble"
      data-testid="figutour-bubble"
      data-figutour-step-id={currentStep.id}
      data-step-index={stepIndex}
      data-place={mobileTopOverride ? "mobile-top" : placeBelow ? "below" : "above"}
      style={{
        position: "fixed",
        left: bubbleLeft,
        top: bubbleTop,
        width: BUBBLE_WIDTH,
        maxWidth: "calc(100vw - 32px)",
        background: "#0a0a0a",
        border: "1px solid #E5B83A",
        borderRadius: 16,
        padding: "14px 18px",
        color: "#ffffff",
        boxShadow:
          "0 0 22px rgba(229, 184, 58, 0.25), 0 4px 14px rgba(0,0,0,0.6)",
        zIndex: 10000,
        opacity: 0,
        animation: "figutour-bubble-in 220ms ease-out forwards",
        fontFamily:
          "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
        pointerEvents: "auto",
      }}
    >
      {/* Header: progress label + skip link */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          data-testid="figutour-progress-label"
          style={{
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(229, 184, 58, 0.85)",
            fontWeight: 600,
          }}
        >
          Step {stepIndex + 1} of {totalSteps}
        </span>
        <button
          type="button"
          data-testid="figutour-skip"
          onClick={onSkipClick}
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.55)",
            fontSize: 11,
            cursor: "pointer",
            padding: 0,
            textDecoration: "underline",
          }}
        >
          {t("figutour.skip_tour", "Skip tour")}
        </button>
      </div>

      <div
        data-testid="figutour-bubble-text"
        style={{ fontSize: 15, lineHeight: 1.6, fontWeight: 500 }}
      >
        {display}
        {!isComplete ? (
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              marginLeft: 1,
              animation: "figutour-caret 1s steps(2, jump-none) infinite",
            }}
          >
            |
          </span>
        ) : null}
      </div>

      {/* Progress dots */}
      <div
        data-testid="figutour-progress"
        style={{ marginTop: 12, display: "flex", gap: 6 }}
        aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
      >
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={i}
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background:
                i === stepIndex ? "#E5B83A" : "rgba(255,255,255,0.22)",
              display: "inline-block",
            }}
          />
        ))}
      </div>

      {/* Actions row */}
      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 10,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          data-testid="figutour-back"
          onClick={onBackClick}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "rgba(255,255,255,0.85)",
            padding: "6px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            visibility: isFirst ? "hidden" : "visible",
            transition: "border-color 150ms, color 150ms",
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          data-testid={isLast ? "figutour-finish" : "figutour-next"}
          onClick={onNextClick}
          style={{
            background: "#E5B83A",
            border: "none",
            color: "#000",
            padding: "6px 16px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            letterSpacing: "0.02em",
          }}
        >
          {isLast ? "Finish" : "Next →"}
        </button>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
